import React from 'react';

import { HistoryTransforms } from '@aline/common';
import { params } from '@aline/routes';
import { Divider, InputLabel } from '@mui/material';
import { FieldRecipientAutoComplete, workflowStepsDialogOpenVar } from 'src/components';
import { TrashcanIcon } from 'src/components/icons/TrashcanIcon';
import { WorkflowIcon } from 'src/components/icons/WorkflowIcon';
import { useMaybeDocumentViewer } from 'src/components/viewer/viewer-context';
import { useMaybeEditorRuntime } from 'src/editor/editor-runtime-registry';
import { useGetFieldsInJSON } from 'src/editor/editor-tab-state-registry';
import { useOnClickWorkflowButton } from 'src/editor/toolbars/editor-app-bars';
import { useDrawerSearchParam } from 'src/editor/toolbars/right-panel/drawer-search-params';
import { ClientTransforms } from 'src/editor/transforms';
import { Apollo } from 'src/gql/generated';
import { useDispatchSuccessSnack, useDispatchUndoSnack, useDocumentParams } from 'src/helpers';
import {
  useDeleteField,
  useDocumentSignatureStatus,
  useIsAuthorizedToEditStorable,
} from 'src/hooks';
import { useMaybeEditor } from 'src/layout-engine';
import type { UserSubset } from 'src/types';
import { makeStyles } from 'tss-react/mui';
import { v4 as uuid4 } from 'uuid';

import { FieldActionButton } from './FieldActionButton';

const useStyles = makeStyles()(theme => ({
  container: {
    padding: theme.spacing(1, 2),
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  formBody: {
    flexGrow: 1,
    overflowY: 'auto',
  },
  inputLabel: {
    ...theme.appTypography.fieldLabel,
    marginBottom: theme.spacing(1),
  },
}));

interface SignatureFieldAction extends Pick<Apollo.WorkflowRecipient, 'id' | '__typename'> {
  assignee: UserSubset | null;
  assigneeRoleName: string | null;
  permission: Pick<Apollo.WorkflowPermission, 'avatarColor' | 'id' | 'isLauncher'>;
  type: Apollo.WorkflowRecipientType.Signer;
}
export interface SignatureFieldFormField extends Pick<
  Apollo.SignatureField,
  'id' | 'nodeId' | '__typename'
> {
  action: SignatureFieldAction | null;
}

interface SignatureFieldPanelProps {
  signatureField: SignatureFieldFormField;
  formDataCy?: string;
}

export function SignatureFieldForm({
  formDataCy,
  signatureField,
}: SignatureFieldPanelProps): JSX.Element | null {
  const { classes } = useStyles();
  const editor = useMaybeEditor();
  const { documentId } = useDocumentParams();
  const documentViewer = useMaybeDocumentViewer();
  const dispatchUndoSnack = useDispatchUndoSnack();
  const { fieldIdsInChildren } = useGetFieldsInJSON();
  const tabId = useMaybeEditorRuntime()?.tabId ?? null;
  const { onClickWorkflowButton } = useOnClickWorkflowButton();
  const { canEdit } = useIsAuthorizedToEditStorable(documentId);
  const { deleteField } = useDeleteField(editor, documentViewer, tabId);
  const [updateSignature] = Apollo.useUpdateSignatureFieldMutation();
  const dispatchUpdateSigSuccessSnack = useDispatchSuccessSnack({
    key: uuid4(),
    message: 'Signature successfully updated.',
  });
  const { setValue: setNewFieldId } = useDrawerSearchParam(params.rightPanel.newFieldId);
  const { setValue: setEditFieldId } = useDrawerSearchParam(params.rightPanel.editFieldId);
  const { isSigned, wasSentForSignature } = useDocumentSignatureStatus(documentId);
  const signatureDisabledReason =
    wasSentForSignature || isSigned
      ? 'Cannot edit during or after signing'
      : !canEdit
        ? 'Not authorized to edit document'
        : '';

  async function handleDeleteSignature(): Promise<void> {
    setEditFieldId(null);
    setNewFieldId(null);

    const { data } = await deleteField(signatureField);
    const isInDocJSON = fieldIdsInChildren.has(signatureField.nodeId);
    if (editor && isInDocJSON) {
      ClientTransforms.focus(editor);
      if (data)
        dispatchUndoSnack('Deleted signature successfully.', () => HistoryTransforms.undo(editor));
    }
  }

  return (
    <div className={classes.form} data-cy={formDataCy}>
      <div className={classes.formBody}>
        <div className={classes.container}>
          <InputLabel className={classes.inputLabel}>Assignee</InputLabel>
          <FieldRecipientAutoComplete
            autoSelectFirstRecipient={false}
            value={signatureField.action}
            onChange={async option => {
              if (!option) return;

              const isNewRecipient = option.__typename === 'NewFieldRecipient';
              if (isNewRecipient) return workflowStepsDialogOpenVar(true);

              const { data } = await updateSignature({
                variables: { input: { id: signatureField.id, props: { actionId: option.id } } },
              });
              if (data) dispatchUpdateSigSuccessSnack();
            }}
          />
        </div>
        <Divider />
        <FieldActionButton
          leftIcon={<WorkflowIcon />}
          text="View signing order"
          onClick={onClickWorkflowButton}
        />
        <FieldActionButton
          disabled={Boolean(signatureDisabledReason)}
          leftIcon={<TrashcanIcon />}
          text="Delete"
          onClick={handleDeleteSignature}
        />
      </div>
    </div>
  );
}

type SignatureFieldActionWithRelaxedType = Omit<SignatureFieldAction, 'type'> & {
  type: Apollo.WorkflowRecipientType;
};
type SignatureFieldWithRelaxedActionType = Omit<SignatureFieldFormField, 'action'> & {
  action: SignatureFieldActionWithRelaxedType | null;
};

export function getIsSignatureFieldFormField(
  field: SignatureFieldWithRelaxedActionType
): field is SignatureFieldFormField {
  return !field.action || field.action.type === Apollo.WorkflowRecipientType.Signer;
}
